import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import StarRating from "@/components/StarRating";
import CafeMap from "@/components/CafeMap";
import { Heart, MapPin, Clock, Globe, Phone, ArrowLeft, Star, ImagePlus, X, ThumbsUp, Flag, MessageCircle } from "lucide-react";
import ClaimButton from "@/components/ClaimButton";
import useDocumentMeta from "@/hooks/useDocumentMeta";
import { toast } from "sonner";

const AMENITY_LABEL = {
  wifi: "Wi-Fi",
  outdoor_seating: "Outdoor seating",
  vegan_milk: "Vegan milk",
  laptop_friendly: "Laptop friendly",
  pet_friendly: "Pet friendly",
  whole_bean: "Whole bean",
  tasting_bar: "Tasting bar",
  classes: "Brewing classes",
  standing_bar: "Standing bar",
  no_laptops: "No laptops",
};

const VIBE_LABEL = {
  quiet_study: "Quiet / study",
  first_date: "First date",
  work_friendly: "Work-friendly",
  espresso_bar: "Espresso bar",
  family: "Family",
  outdoor: "Outdoor",
};

const typeLabel = (t) => (t === "cafe" ? "Café" : t === "bean_shop" ? "Bean shop" : "Café · Bean shop");

export default function CafeDetailPage() {
  const { id } = useParams();
  const { user, favorites, toggleFavorite } = useAuth();
  const [cafe, setCafe] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [images, setImages] = useState([]); // base64 data URLs
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: c }, { data: r }] = await Promise.all([
        api.get(`/cafes/${id}`),
        api.get(`/cafes/${id}/reviews`),
      ]);
      setCafe(c);
      setReviews(r);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const isFav = cafe && favorites?.has(cafe.id);
  const myReview = user && reviews.find((r) => r.user_id === user.id);
  const isOwner = !!(user && cafe && (cafe.owner_user_id === user.id || user.role === "admin"));

  useDocumentMeta({
    title: cafe ? `${cafe.name} · ${cafe.city}` : undefined,
    description: cafe ? cafe.description?.slice(0, 160) : undefined,
  });

  const postOwnerReply = async (review) => {
    const text = window.prompt(`Reply as the owner of ${cafe.name}:`);
    if (!text || text.trim().length < 3) return;
    try {
      const { data } = await api.post(`/cafes/${cafe.id}/reviews/${review.id}/owner-reply`, { text });
      setReviews((prev) => prev.map((r) => r.id === review.id ? { ...r, owner_reply: data } : r));
      toast("Reply posted.");
    } catch (e) {
      toast("Couldn't reply", { description: e.response?.data?.detail || e.message });
    }
  };

  const deleteOwnerReply = async (review) => {
    if (!window.confirm("Remove your reply?")) return;
    try {
      await api.delete(`/cafes/${cafe.id}/reviews/${review.id}/owner-reply`);
      setReviews((prev) => prev.map((r) => r.id === review.id ? { ...r, owner_reply: undefined } : r));
      toast("Reply removed.");
    } catch (e) {
      toast("Couldn't remove", { description: e.response?.data?.detail || e.message });
    }
  };

  useEffect(() => {
    if (myReview) {
      setRating(myReview.rating);
      setComment(myReview.comment);
      setImages(myReview.images || []);
    }
  }, [myReview]);

  const resizeImage = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const max = 1200;
        let { width, height } = img;
        if (width > max || height > max) {
          const ratio = Math.min(max / width, max / height);
          width = Math.round(width * ratio);
          height = Math.round(height * ratio);
        }
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        canvas.getContext("2d").drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL("image/jpeg", 0.82));
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const onImagePick = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, 3 - images.length);
    e.target.value = "";
    for (const f of files) {
      if (!f.type.startsWith("image/")) continue;
      try {
        const dataUrl = await resizeImage(f);
        setImages((prev) => [...prev, dataUrl].slice(0, 3));
      } catch {
        toast("Couldn't read image", { description: f.name });
      }
    }
  };

  const removeImage = (idx) => setImages((prev) => prev.filter((_, i) => i !== idx));

  const submitReview = async (e) => {
    e.preventDefault();
    if (!user) { toast("Please sign in to review."); return; }
    if (rating < 1) { toast("Pick a star rating."); return; }
    if (comment.trim().length < 3) { toast("Add a short comment."); return; }
    setSubmitting(true);
    try {
      await api.post(`/cafes/${id}/reviews`, { rating, comment, images });
      toast("Review posted. Thanks for sharing.");
      await load();
    } catch (e) {
      toast("Couldn't post review", { description: e.response?.data?.detail || e.message });
    } finally {
      setSubmitting(false);
    }
  };

  const handleFav = async () => {
    if (!user) { toast("Sign in to save favorites."); return; }
    const r = await toggleFavorite(cafe.id);
    if (r.ok) toast(r.favorited ? "Added to favorites" : "Removed from favorites");
  };

  const toggleHelpful = async (review) => {
    if (!user) { toast("Sign in to vote on reviews."); return; }
    try {
      const { data } = await api.post(`/reviews/${review.id}/helpful`);
      setReviews((prev) => prev.map((r) =>
        r.id === review.id ? { ...r, helpful_count: data.helpful_count, i_helpful: data.helpful } : r
      ));
    } catch (e) {
      toast("Couldn't vote", { description: e.response?.data?.detail || e.message });
    }
  };

  const reportTarget = async (target_type, target_id, label) => {
    if (!user) { toast("Sign in to report content."); return; }
    const reason = window.prompt(`Why are you reporting this ${label}? (We'll review it.)`);
    if (!reason || reason.trim().length < 3) return;
    try {
      await api.post("/reports", { target_type, target_id, reason });
      toast("Thanks — we'll take a look.");
    } catch (e) {
      toast("Couldn't submit report", { description: e.response?.data?.detail || e.message });
    }
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
        <div className="h-[400px] bg-card border border-border rounded-sm animate-pulse" />
      </div>
    );
  }
  if (!cafe) {
    return (
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 text-center">
        <p className="font-serif text-3xl">Café not found.</p>
        <Link to="/directory" className="text-[#C05A46] mt-4 inline-block">← Back to directory</Link>
      </div>
    );
  }

  return (
    <div data-testid="cafe-detail-page" className="pb-24">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-6">
        <Link to="/directory" className="text-xs uppercase tracking-wider text-[#786B63] hover:text-[#C05A46] inline-flex items-center gap-1">
          <ArrowLeft size={14} strokeWidth={1.5} /> Back to directory
        </Link>
      </div>

      {/* Hero */}
      <div className="max-w-7xl mx-auto px-6 lg:px-10 mt-6">
        <div className="border border-border bg-card rounded-sm overflow-hidden">
          <div className="aspect-[16/7] bg-[#E8DFD3] overflow-hidden">
            {cafe.image_url && (
              <img src={cafe.image_url} alt={cafe.name} className="w-full h-full object-cover" />
            )}
          </div>
          <div className="p-8 md:p-10 grid md:grid-cols-[1fr_auto] gap-6 items-start">
            <div>
              <span className="text-[10px] uppercase tracking-[0.2em] px-2 py-1 border border-border rounded-full text-[#786B63]">
                {typeLabel(cafe.type)}
              </span>
              <h1 className="font-serif text-5xl sm:text-6xl mt-3 leading-[1.05]">{cafe.name}</h1>
              <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mt-4 text-sm text-[#786B63]">
                <span className="inline-flex items-center gap-1.5">
                  <MapPin size={14} strokeWidth={1.5} /> {cafe.address}, {cafe.city}
                </span>
                {cafe.hours && <span className="inline-flex items-center gap-1.5"><Clock size={14} strokeWidth={1.5} /> {cafe.hours}</span>}
                {cafe.website && (
                  <a href={cafe.website} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 hover:text-[#C05A46]">
                    <Globe size={14} strokeWidth={1.5} /> Website
                  </a>
                )}
                {cafe.phone && <span className="inline-flex items-center gap-1.5"><Phone size={14} strokeWidth={1.5} /> {cafe.phone}</span>}
                <span>{cafe.price_range || "$$"}</span>
              </div>
              <div className="mt-4 flex items-center gap-3">
                <StarRating rating={cafe.rating_avg} size={18} showValue />
                <span className="text-sm text-[#786B63]">
                  · {cafe.review_count} review{cafe.review_count === 1 ? "" : "s"}
                </span>
              </div>
            </div>
            <Button
              onClick={handleFav}
              data-testid="detail-favorite-button"
              variant="outline"
              className="rounded-sm border-border h-11 gap-2"
            >
              <Heart size={16} strokeWidth={1.5} className={isFav ? "fill-[#C05A46] text-[#C05A46]" : ""} />
              {isFav ? "Saved" : "Save"}
            </Button>
          </div>
        </div>
      </div>

      {/* Content split */}
      <div className="max-w-7xl mx-auto px-6 lg:px-10 mt-10 grid md:grid-cols-[1fr_420px] gap-8">
        <div>
          <div className="border border-border bg-card rounded-sm p-8">
            <h2 className="font-serif text-3xl">About</h2>
            <p className="text-[#2C1E16]/90 mt-4 leading-relaxed">{cafe.description}</p>

            {cafe.specialties?.length > 0 && (
              <>
                <p className="mt-8 text-xs uppercase tracking-[0.25em] text-[#786B63]">Specialties</p>
                <div className="flex flex-wrap gap-2 mt-3">
                  {cafe.specialties.map((s) => (
                    <span key={s} className="text-xs px-3 py-1.5 border border-border rounded-full bg-[#F9F6F0] text-[#2C1E16]">
                      {s}
                    </span>
                  ))}
                </div>
              </>
            )}

            {cafe.amenities?.length > 0 && (
              <>
                <p className="mt-8 text-xs uppercase tracking-[0.25em] text-[#786B63]">Amenities</p>
                <div className="flex flex-wrap gap-2 mt-3">
                  {cafe.amenities.map((a) => (
                    <span key={a} className="text-xs px-3 py-1.5 border border-border rounded-full bg-[#F2EBE1] text-[#786B63] uppercase tracking-wider">
                      {AMENITY_LABEL[a] || a}
                    </span>
                  ))}
                </div>
              </>
            )}

            {cafe.vibe_tags?.length > 0 && (
              <>
                <p className="mt-8 text-xs uppercase tracking-[0.25em] text-[#786B63]">Vibe</p>
                <div className="flex flex-wrap gap-2 mt-3">
                  {cafe.vibe_tags.map((v) => (
                    <span key={v} className="text-xs px-3 py-1.5 border border-[#C05A46]/40 rounded-full bg-[#C05A46]/8 text-[#C05A46] uppercase tracking-wider">
                      {VIBE_LABEL[v] || v}
                    </span>
                  ))}
                </div>
              </>
            )}

            <button
              type="button"
              onClick={() => reportTarget("cafe", cafe.id, "café")}
              data-testid="report-cafe-btn"
              className="text-xs text-[#786B63] hover:text-[#C05A46] mt-8 inline-flex items-center gap-1.5"
            >
              <Flag size={12} strokeWidth={1.5} /> Report this listing
            </button>
            <div className="mt-3">
              <ClaimButton cafe={cafe} />
            </div>
          </div>

          {/* Reviews */}
          <div className="mt-10">
            <div className="flex items-end justify-between mb-6">
              <h2 className="font-serif text-3xl">Reviews</h2>
              <span className="text-sm text-[#786B63]">{reviews.length} total</span>
            </div>

            {/* Write review */}
            <form
              onSubmit={submitReview}
              className="border border-border bg-card rounded-sm p-6 mb-8"
              data-testid="review-form"
            >
              <p className="text-xs uppercase tracking-[0.25em] text-[#786B63]">
                {myReview ? "Edit your review" : "Share your experience"}
              </p>
              <div className="flex items-center gap-1 mt-3">
                {[1, 2, 3, 4, 5].map((s) => (
                  <button
                    type="button"
                    key={s}
                    onMouseEnter={() => setHover(s)}
                    onMouseLeave={() => setHover(0)}
                    onClick={() => setRating(s)}
                    data-testid={`review-star-${s}`}
                    className="p-1"
                  >
                    <Star
                      size={22}
                      strokeWidth={1.5}
                      className={
                        (hover || rating) >= s
                          ? "fill-[#C05A46] text-[#C05A46]"
                          : "text-[#C05A46]/40"
                      }
                    />
                  </button>
                ))}
                <span className="text-sm text-[#786B63] ml-2">{rating ? `${rating}/5` : "Pick a rating"}</span>
              </div>
              <Textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="What was the espresso like? The space? Worth coming back?"
                rows={4}
                className="mt-4 rounded-sm bg-[#F9F6F0] border-border"
                data-testid="review-comment"
              />

              {/* Photo uploader */}
              <div className="mt-4">
                <div className="flex flex-wrap gap-3">
                  {images.map((src, i) => (
                    <div key={i} className="relative w-20 h-20 rounded-sm overflow-hidden border border-border bg-[#F9F6F0]">
                      <img src={src} alt="" className="w-full h-full object-cover" />
                      <button
                        type="button"
                        onClick={() => removeImage(i)}
                        data-testid={`remove-photo-${i}`}
                        className="absolute top-1 right-1 h-5 w-5 grid place-items-center bg-[#2C1E16]/80 text-[#F9F6F0] rounded-full"
                        aria-label="Remove photo"
                      >
                        <X size={12} strokeWidth={2} />
                      </button>
                    </div>
                  ))}
                  {images.length < 3 && (
                    <label
                      data-testid="review-photo-upload"
                      className="w-20 h-20 grid place-items-center border border-dashed border-border rounded-sm cursor-pointer hover:bg-[#F2EBE1] text-[#786B63]"
                    >
                      <ImagePlus size={20} strokeWidth={1.5} />
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={onImagePick}
                        className="hidden"
                      />
                    </label>
                  )}
                </div>
                <p className="text-xs text-[#786B63] mt-2">Add up to 3 photos · auto-resized.</p>
              </div>

              <Button
                type="submit"
                disabled={submitting}
                className="mt-4 rounded-sm bg-[#C05A46] hover:bg-[#A34A38] text-[#F9F6F0]"
                data-testid="review-submit"
              >
                {submitting ? "Posting…" : myReview ? "Update review" : "Post review"}
              </Button>
              {!user && (
                <p className="text-xs text-[#786B63] mt-3">
                  <Link to="/login" className="text-[#C05A46]">Sign in</Link> to leave a review.
                </p>
              )}
            </form>

            <div className="space-y-5">
              {reviews.length === 0 ? (
                <p className="text-sm text-[#786B63]">No reviews yet — be the first.</p>
              ) : reviews.map((r) => (
                <div key={r.id} className="border border-border bg-card rounded-sm p-5" data-testid={`review-${r.id}`}>
                  <div className="flex items-center gap-3">
                    <span className="h-8 w-8 rounded-full bg-[#C05A46] text-[#F9F6F0] grid place-items-center text-xs font-medium">
                      {r.user_name?.[0]?.toUpperCase() || "U"}
                    </span>
                    <div className="flex-1">
                      <p className="text-sm text-[#2C1E16] font-medium">{r.user_name}</p>
                      <div className="flex items-center gap-2 text-xs text-[#786B63]">
                        <StarRating rating={r.rating} size={11} />
                        <span>· {new Date(r.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                  </div>
                  <p className="text-sm text-[#2C1E16]/90 mt-3 leading-relaxed">{r.comment}</p>
                  {r.images && r.images.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {r.images.map((src, i) => (
                        <a
                          key={i}
                          href={src}
                          target="_blank"
                          rel="noreferrer"
                          className="block w-24 h-24 rounded-sm overflow-hidden border border-border"
                          data-testid={`review-image-${r.id}-${i}`}
                        >
                          <img src={src} alt="" className="w-full h-full object-cover" />
                        </a>
                      ))}
                    </div>
                  )}
                  <div className="mt-3 flex items-center gap-4">
                    <button
                      type="button"
                      onClick={() => toggleHelpful(r)}
                      data-testid={`helpful-${r.id}`}
                      className={`text-xs inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border transition-colors ${
                        r.i_helpful
                          ? "border-[#C05A46] bg-[#C05A46] text-[#F9F6F0]"
                          : "border-border bg-[#F9F6F0] text-[#786B63] hover:border-[#C05A46]"
                      }`}
                    >
                      <ThumbsUp size={11} strokeWidth={1.5} />
                      Helpful · {r.helpful_count || 0}
                    </button>
                    <button
                      type="button"
                      onClick={() => reportTarget("review", r.id, "review")}
                      data-testid={`report-review-${r.id}`}
                      className="text-xs text-[#786B63] hover:text-[#C05A46] inline-flex items-center gap-1.5"
                    >
                      <Flag size={11} strokeWidth={1.5} /> Report
                    </button>
                    {isOwner && !r.owner_reply && (
                      <button
                        type="button"
                        onClick={() => postOwnerReply(r)}
                        data-testid={`owner-reply-${r.id}`}
                        className="text-xs text-[#C05A46] hover:underline inline-flex items-center gap-1.5"
                      >
                        <MessageCircle size={11} strokeWidth={1.5} /> Reply as owner
                      </button>
                    )}
                  </div>
                  {r.owner_reply && (
                    <div className="mt-4 ml-6 pl-4 border-l-2 border-[#C05A46] bg-[#C05A46]/5 rounded-r-sm p-4" data-testid={`owner-reply-block-${r.id}`}>
                      <p className="text-xs uppercase tracking-[0.2em] text-[#C05A46] inline-flex items-center gap-1.5">
                        <MessageCircle size={11} strokeWidth={1.5} />
                        Owner's reply · {r.owner_reply.user_name}
                      </p>
                      <p className="text-sm text-[#2C1E16]/90 mt-2 leading-relaxed">{r.owner_reply.text}</p>
                      {isOwner && (
                        <button
                          type="button"
                          onClick={() => deleteOwnerReply(r)}
                          data-testid={`delete-owner-reply-${r.id}`}
                          className="text-xs text-[#786B63] hover:text-[#C05A46] mt-2"
                        >
                          Remove reply
                        </button>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right: Map */}
        <div className="md:sticky md:top-20 self-start">
          <CafeMap cafes={[cafe]} height="h-[420px]" />
          <div className="mt-3 text-xs text-[#786B63]">
            <p className="uppercase tracking-[0.2em]">Find it</p>
            <p className="mt-1 text-sm text-[#2C1E16]">{cafe.address}, {cafe.city}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
